// src/pages/reportes/components/ReporteCliente.tsx
import { Card, Spinner, Badge } from '@/components/ui';
import {
  Building2,
  Ticket,
  MessageSquare,
  Clock,
  AlertCircle,
  Target,
} from 'lucide-react';
import { useReporteCliente } from '@/hooks/queries/useReportes';
import { PeriodoReporte } from '@/types/reporte';
import type { ReporteCliente as ReporteClienteType } from '@/types/reporte';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ReporteClienteProps {
  clienteId: string;
  periodo: PeriodoReporte;
}

export function ReporteCliente({ clienteId, periodo }: ReporteClienteProps) {
  const { data: response, isLoading, error } = useReporteCliente(clienteId, periodo);

  if (!clienteId) {
    return (
      <Card className="p-6">
        <div className="text-center text-gray-500">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>Selecciona un cliente para ver su reporte</p>
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-red-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-4" />
          <p>Error al cargar el reporte del cliente</p>
        </div>
      </Card>
    );
  }

  if (!response) {
    return (
      <Card className="p-6">
        <p className="text-center text-gray-500">No hay datos disponibles para este cliente</p>
      </Card>
    );
  }

  // Extraer datos del reporte
  const reporte = response.datos as unknown as ReporteClienteType | undefined;

  if (!reporte) {
    return (
      <Card className="p-6">
        <p className="text-center text-gray-500">No hay datos disponibles para este cliente</p>
      </Card>
    );
  }

  const formatFecha = (fecha: string) => {
    try {
      return format(new Date(fecha), 'dd MMM yyyy', { locale: es });
    } catch {
      return fecha;
    }
  };

  return (
    <div className="space-y-6">
      {/* Info del cliente */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{reporte.cliente?.nombre ?? response.nombre ?? 'Cliente'}</h2>
            <p className="text-sm text-gray-500">
              Periodo: {formatFecha(reporte.periodo?.inicio ?? '')} - {formatFecha(reporte.periodo?.fin ?? '')}
            </p>
          </div>
        </div>
      </Card>

      {/* Resumen de metricas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{reporte.resumen?.tickets_abiertos ?? 0}</p>
            <p className="text-sm text-gray-500">Tickets Abiertos</p>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-indigo-600">{reporte.resumen?.tickets_periodo ?? 0}</p>
            <p className="text-sm text-gray-500">Tickets en Periodo</p>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{reporte.resumen?.tickets_resueltos ?? 0}</p>
            <p className="text-sm text-gray-500">Tickets Resueltos</p>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-600">{reporte.resumen?.conversaciones_activas ?? 0}</p>
            <p className="text-sm text-gray-500">Conversaciones Activas</p>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">{(reporte.resumen?.horas_dedicadas ?? 0).toFixed(1)}h</p>
            <p className="text-sm text-gray-500">Horas Dedicadas</p>
          </div>
        </Card>
      </div>

      {/* Detalles de Tickets */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5" />
          Detalle de Tickets
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Por Estado */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Por Estado</h4>
            <div className="space-y-2">
              {reporte.tickets?.por_estado && Object.entries(reporte.tickets.por_estado).map(([estado, cantidad]) => (
                <div key={estado} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600 capitalize">
                    {estado.toLowerCase().replace('_', ' ')}
                  </span>
                  <Badge variant="default">{String(cantidad)}</Badge>
                </div>
              ))}
              {(!reporte.tickets?.por_estado || Object.keys(reporte.tickets.por_estado).length === 0) && (
                <p className="text-sm text-gray-500">Sin tickets en el periodo</p>
              )}
            </div>
          </div>

          {/* Por Prioridad */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Por Prioridad</h4>
            <div className="space-y-2">
              {reporte.tickets?.por_prioridad && Object.entries(reporte.tickets.por_prioridad).map(([prioridad, cantidad]) => {
                const colorMap: Record<string, string> = {
                  CRITICA: 'bg-red-100 text-red-800',
                  ALTA: 'bg-orange-100 text-orange-800',
                  MEDIA: 'bg-yellow-100 text-yellow-800',
                  BAJA: 'bg-green-100 text-green-800',
                };
                return (
                  <div key={prioridad} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-600 capitalize">{prioridad.toLowerCase()}</span>
                    <Badge className={colorMap[prioridad] || ''}>{String(cantidad)}</Badge>
                  </div>
                );
              })}
              {(!reporte.tickets?.por_prioridad || Object.keys(reporte.tickets.por_prioridad).length === 0) && (
                <p className="text-sm text-gray-500">Sin tickets en el periodo</p>
              )}
            </div>
          </div>
        </div>

        {/* Metricas */}
        <div className="mt-6 pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Clock className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Tiempo promedio resolución</p>
              <p className="text-lg font-semibold">
                {Math.round(reporte.tickets?.tiempo_promedio_resolucion_min ?? 0)} minutos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Target className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">SLA cumplido</p>
              <p className="text-lg font-semibold">{(reporte.tickets?.sla_cumplido_pct ?? 0).toFixed(1)}%</p>
            </div>
          </div>
        </div>

        {/* Tendencia */}
        {reporte.tickets?.tendencia && reporte.tickets.tendencia.length > 0 && (
          <div className="mt-6 pt-4 border-t">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Tendencia de tickets</h4>
            <div className="h-24 flex items-end gap-1">
              {reporte.tickets.tendencia.slice(-14).map((item, idx) => {
                const maxVal = Math.max(...(reporte.tickets?.tendencia ?? []).map((t) => t.valor), 1);
                return (
                  <div
                    key={idx}
                    className="flex-1 bg-blue-400 rounded-t hover:bg-blue-500 transition-colors"
                    style={{
                      height: `${Math.max(10, (item.valor / maxVal) * 100)}%`,
                    }}
                    title={`${item.fecha}: ${item.valor} tickets`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Detalles de Conversaciones */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Detalle de Conversaciones
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-6 bg-blue-50 rounded-lg">
            <MessageSquare className="h-10 w-10 text-blue-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-blue-600">{reporte.conversaciones?.activas ?? 0}</p>
            <p className="text-sm text-gray-500">Conversaciones Activas</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Por Estado</h4>
            <div className="space-y-2">
              {reporte.conversaciones?.por_estado && Object.entries(reporte.conversaciones.por_estado).map(([estado, cantidad]) => (
                <div key={estado} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600 capitalize">
                    {estado.toLowerCase().replace('_', ' ')}
                  </span>
                  <Badge variant="default">{String(cantidad)}</Badge>
                </div>
              ))}
              {(!reporte.conversaciones?.por_estado || Object.keys(reporte.conversaciones.por_estado).length === 0) && (
                <p className="text-sm text-gray-500">Sin conversaciones en el periodo</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Tiempo dedicado */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Tiempo Dedicado
        </h3>
        <div className="flex items-center gap-4">
          <div className="p-4 bg-purple-100 rounded-lg">
            <Clock className="h-8 w-8 text-purple-600" />
          </div>
          <div>
            <p className="text-4xl font-bold text-purple-600">
              {(reporte.tiempo?.horas_totales ?? 0).toFixed(1)}
            </p>
            <p className="text-gray-500">horas totales en el periodo</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
